import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import './HeadlineModal.scss';

import deleteIcon from './img/DeleteHeadline.png';
import shareIcon from './img/share.png';
import moreIcon from './img/more.png';
import linkIcon from './img/link.png';
import reportIcon from './img/report.png';

import viewIcon from './img/view.png';
import sendIcon from './img/send.png';

import ShareModal from '../Shared/ShareModal';
import '../Shared/ShareModal.scss';
import ReportsModal from '../Reports/Reports';
import Alerts from '../Alerts/Alerts';

import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getAccessToken } from '../../awsAuth';
import { useSubscription } from '../../contexts/SubscriptionContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const STORY_DURATION_MS = 10000;

function pluralUa(n, one, few, many) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) return few;
  return many;
}

function timeAgoSmart(tsMs, nowMs) {
  const base = typeof nowMs === 'number' ? nowMs : Date.now();
  const diffSec = Math.max(0, Math.floor((base - (tsMs || 0)) / 1000));

  if (diffSec < 30) return 'щойно';

  const m = Math.floor(diffSec / 60);
  if (m <= 1) return 'хвилину тому';
  if (m < 60) return `${m} ${pluralUa(m, 'хвилину', 'хвилини', 'хвилин')} тому`;

  const h = Math.floor(m / 60);
  if (h <= 1) return 'годину тому';
  if (h < 24) return `${h} ${pluralUa(h, 'годину', 'години', 'годин')} тому`;

  const d = Math.floor(h / 24);
  if (d <= 1) return 'вчора';
  return `${d} ${pluralUa(d, 'день', 'дні', 'днів')} тому`;
}

/* Gradient generator (not a hook) */
function getGradient(seed) {
  let h = 0;
  const s = String(seed || '');
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const rnd = (n) =>
    ((h = (h ^ (h << 13)) >>> 0),
    (h = (h ^ (h >>> 17)) >>> 0),
    (h = (h ^ (h << 5)) >>> 0),
    h & 0xfffffff) % n;
  const blue = 200 + rnd(20);
  const light = 240 + rnd(10);
  return `linear-gradient(160deg,
           rgba(255,255,255,0.85) 0%,
           rgba(${blue}, ${220 + rnd(20)}, 255, 0.28) 40%,
           rgba(${light}, ${light}, 255, 0.18) 100%)`;
}

/* Seen state (localStorage) */
function readSeen(authorId) {
  try {
    const raw = localStorage.getItem(`hl_seen_author_${authorId}`);
    if (!raw) return { viewedCount: 0, nextIndex: 0, completedAt: 0 };
    const o = JSON.parse(raw);
    return {
      viewedCount: Math.max(0, Number(o?.viewedCount ?? 0)),
      nextIndex: Math.max(0, Number(o?.nextIndex ?? 0)),
      completedAt: Math.max(0, Number(o?.completedAt ?? 0)),
    };
  } catch {
    return { viewedCount: 0, nextIndex: 0, completedAt: 0 };
  }
}
function writeSeen(authorId, patch) {
  try {
    const cur = readSeen(authorId);
    const next = { ...cur, ...patch };
    localStorage.setItem(`hl_seen_author_${authorId}`, JSON.stringify(next));
  } catch {}
}

const clampNum = (v, min, max, fallback) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
};

export default function HeadlineModal({
  headline,
  carouselItems = null,
  initialIndex = 0,
  isAuthor = false,
  onClose,
  onRespond,
  onDelete,
  onGoProfile,
}) {
  const { userId } = useAuth() || {};
  const { isPremium: isPremiumFromCtx } = useSubscription();
  const isPremium = !!isPremiumFromCtx;

  useEffect(() => {
    const body = document.body;
    const prevOverflow = body.style.overflow;
    const prevTouchAction = body.style.touchAction;

    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';

    return () => {
      body.style.overflow = prevOverflow || '';
      body.style.touchAction = prevTouchAction || '';
    };
  }, []);

  const rawItems = useMemo(() => {
    if (Array.isArray(carouselItems) && carouselItems.length > 0) return carouselItems;
    if (headline) return [headline];
    return [];
  }, [carouselItems, headline]);

  const items = useMemo(
    () => [...rawItems].sort((a, b) => Number(a?.createdAt || 0) - Number(b?.createdAt || 0)),
    [rawItems]
  );

  const authorId = items[0]?.userId ?? headline?.userId ?? undefined;
  const itemsLen = items.length;

  const [idx, setIdx] = useState(() => {
    if (!authorId) return Math.max(0, Math.min(initialIndex, Math.max(itemsLen - 1, 0)));
    const { viewedCount, nextIndex } = readSeen(authorId);
    if (itemsLen > 0 && viewedCount >= itemsLen) return 0;
    return Math.max(0, Math.min(nextIndex, Math.max(itemsLen - 1, 0)));
  });

  const cur = items[idx] || headline || {};
  const {
    id,
    title,
    createdAt,
    userId: itemAuthorId,
    authorName,
    authorAvatar,
    accountType,
    viewsCount,
  } = cur;

  const displayTitle = useMemo(() => (title || '').slice(0, 140).trim(), [title]);

  const cityText = useMemo(() => {
    const t = String(cur?.citySlug || cur?.location || cur?.city || '').trim();
    return t;
  }, [cur]);

  const photoUrl = useMemo(() => {
    const p =
      cur?.photoUrl ||
      cur?.photo ||
      cur?.imageUrl ||
      cur?.image ||
      cur?.coverUrl ||
      cur?.mediaUrl ||
      '';
    return String(p || '').trim();
  }, [cur]);

  const gradient = useMemo(() => getGradient(id || itemAuthorId || title), [id, itemAuthorId, title]);

  const isMulti = itemsLen > 1;

  const goPrev = useCallback(() => {
    setIdx((p) => (p - 1 + itemsLen) % itemsLen);
  }, [itemsLen]);

  const goNext = useCallback(() => {
    setIdx((p) => (p + 1) % itemsLen);
  }, [itemsLen]);

  const [pressPaused, setPressPaused] = useState(false);
  const [replyMode, setReplyMode] = useState(false);
  const isPaused = pressPaused || replyMode;

  const storyRef = useRef(null);
  const backdropRef = useRef(null);

  const [swipeClosing, setSwipeClosing] = useState(false);
  const [swipeY, setSwipeY] = useState(0);
  const [swipeDim, setSwipeDim] = useState(0);
  const swipeRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    startedAt: 0,
    directionLocked: false,
    isVertical: false,
    moved: false,
    shouldIgnore: false,
    height: 720,
  });

  const isCoarsePointer = () => {
    try {
      return !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    } catch {
      return false;
    }
  };

  const isInteractiveTarget = (target) => {
  if (!target) return false;
  const el = target.closest?.(
    'a, input, textarea, select, [role="menu"], [role="menuitem"], .hlm__replyBar, .hlm__replyGlass, .hlm__menu, .hlm__portal-stop, .hlm__topBtn, .hlm__closeBtn, .hlm__avaBtn, .hlm__authorBtn, .hlm__sendBtn'
  );
  return !!el;
};

  const resetSwipeVisual = (withAnim = true) => {
    if (!storyRef.current) return;

    if (withAnim) storyRef.current.classList.add('hlm__swipe-back');
    setSwipeClosing(false);
    setSwipeY(0);
    setSwipeDim(0);

    window.setTimeout(() => {
      try { storyRef.current?.classList.remove('hlm__swipe-back'); } catch {}
    }, 220);
  };

  useEffect(() => {
    resetSwipeVisual(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  useEffect(() => {
    if (!isMulti) return;
    const onKey = (e) => {
      if (replyMode) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMulti, goPrev, goNext, replyMode]);

  const sessionSeenMaxRef = useRef(-1);
  const markSessionSeen = useCallback((i) => {
    if (typeof i !== 'number') return;
    sessionSeenMaxRef.current = Math.max(sessionSeenMaxRef.current, i);
  }, []);
  useEffect(() => {
    markSessionSeen(idx);
  }, [idx, markSessionSeen]);

  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  const [viewsById, setViewsById] = useState({});
  const displayedViews = useMemo(() => {
    const base = Number(viewsCount ?? 0);
    const override = viewsById[id || ''];
    return Number.isFinite(override) ? override : base;
  }, [viewsCount, viewsById, id]);

  useEffect(() => {
    let cancelled = false;
    async function fetchTotalForAuthor() {
      const entityId = id || headline?.id;
      if (!entityId || !isAuthor) return;
      try {
        const { data } = await axios.get(`${API_BASE}/api/views/unique`, {
          params: { entityType: 'headline', entityId },
          timeout: 8000,
        });
        if (cancelled) return;
        const total = Number(data?.total);
        if (Number.isFinite(total)) setViewsById((p) => ({ ...p, [entityId]: total }));
      } catch {}
    }
    fetchTotalForAuthor();
    return () => {
      cancelled = true;
    };
  }, [id, headline?.id, isAuthor]);

  useEffect(() => {
    let cancelled = false;
    async function registerView() {
      const entityId = id || headline?.id;
      if (!entityId) return;
      if (!userId) return;
      if (isAuthor) return;
      if (userId === itemAuthorId) return;
      try {
        const { data } = await axios.post(`${API_BASE}/api/views/unique`, {
          entityType: 'headline',
          entityId,
          userId,
          createdAt,
        });
        if (cancelled) return;
        if (data?.incremented === 1) {
          setViewsById((prev) => {
            const baseLocal = Number(prev[entityId] ?? viewsCount ?? 0);
            const next = Number.isFinite(baseLocal) ? baseLocal + 1 : 1;
            return { ...prev, [entityId]: next };
          });
        } else if (typeof data?.total === 'number' && Number.isFinite(data.total)) {
          setViewsById((prev) => ({ ...prev, [entityId]: data.total }));
        }
      } catch {}
    }
    registerView();
    return () => {
      cancelled = true;
    };
  }, [id, headline?.id, createdAt, userId, isAuthor, itemAuthorId, viewsCount]);

  const handleClose = useCallback(() => {
    if (authorId) {
      const prev = readSeen(authorId);
      const maxIndexSeen = Math.max(prev.viewedCount - 1, sessionSeenMaxRef.current);
      const nextViewedCount = Math.max(prev.viewedCount, maxIndexSeen + 1, 0);
      const bounded = Math.min(nextViewedCount, Math.max(itemsLen, 0));
      const nextIndex =
        bounded >= itemsLen && itemsLen > 0
          ? 0
          : Math.min(Math.max(bounded, 0), Math.max(itemsLen - 1, 0));

      writeSeen(authorId, {
        viewedCount: bounded,
        nextIndex,
        completedAt:
          bounded >= itemsLen && itemsLen > 0 ? Date.now() : readSeen(authorId).completedAt || 0,
      });

      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('cvanto_headline_seen', {
              detail: { authorId, viewedCount: bounded, total: itemsLen },
            })
          );
        }
      } catch {}
    }

    onClose?.();
  }, [authorId, itemsLen, onClose]);

  const onBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) handleClose();
    },
    [handleClose]
  );

  const [elapsedMs, setElapsedMs] = useState(0);
  const rafRef = useRef(null);
  const lastTRef = useRef(0);

  useEffect(() => {
    setElapsedMs(0);
    lastTRef.current = 0;
  }, [idx]);

  useEffect(() => {
    const loop = (t) => {
      rafRef.current = requestAnimationFrame(loop);

      if (isPaused) {
        lastTRef.current = t;
        return;
      }

      if (!lastTRef.current) lastTRef.current = t;
      const dt = t - lastTRef.current;
      lastTRef.current = t;

      if (dt <= 0 || !Number.isFinite(dt)) return;

      setElapsedMs((prev) => {
        const next = prev + dt;
        if (next >= STORY_DURATION_MS) {
          if (isMulti) {
            setIdx((p) => (p + 1) % itemsLen);
            return 0;
          }
          handleClose();
          return prev;
        }
        return next;
      });
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      try {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      } catch {}
      rafRef.current = null;
    };
  }, [isPaused, isMulti, itemsLen, handleClose]);

  const [shareOpen, setShareOpen] = useState(false);
  const shareUrl = useMemo(() => {
    const hid = id || headline?.id || '';
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/headline/${encodeURIComponent(hid)}`;
  }, [id, headline?.id]);
  const shareText = displayTitle;

  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [authAlertOpen, setAuthAlertOpen] = useState(false);

  const handleNewOrUpgrade = useCallback(() => {
    if (isAuthor) {
      setUpgradeOpen(true);
      return;
    }
    try {
      onClose?.();
    } catch {}
    setTimeout(() => {
      onRespond?.({ type: 'create_headline' });
    }, 0);
  }, [isAuthor, onClose, onRespond]);

  const openAuthAlert = useCallback(() => {
  setAuthAlertOpen(true);
}, []);

  const [menuOpen, setMenuOpen] = useState(false);
  const moreBtnRef = useRef(null);
  const menuRef = useRef(null);
  const toggleMenu = useCallback(() => setMenuOpen((v) => !v), []);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  useEffect(() => {
    if (!menuOpen) return;
    function onDoc(e) {
      if (!menuRef.current) return;
      if (menuRef.current.contains(e.target)) return;
      if (moreBtnRef.current && moreBtnRef.current.contains(e.target)) return;
      setMenuOpen(false);
    }
    function onEsc(e) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [menuOpen]);

  const [copyState, setCopyState] = useState({ ok: false, error: '' });
  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyState({ ok: true, error: '' });
      setTimeout(() => setCopyState({ ok: false, error: '' }), 1800);
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = shareUrl;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopyState({ ok: true, error: '' });
        setTimeout(() => setCopyState({ ok: false, error: '' }), 1800);
      } catch {
        setCopyState({ ok: false, error: 'Не вдалося скопіювати' });
        setTimeout(() => setCopyState({ ok: false, error: '' }), 2200);
      }
    }
  }, [shareUrl]);

  const [showReportUniversal, setShowReportUniversal] = useState(false);
  const openReport = useCallback(() => {
  if (!userId) {
    openAuthAlert();
    return;
  }
  setShowReportUniversal(true);
}, [userId, openAuthAlert]);

  const menuItems = useMemo(() => {
    const arr = [
      { key: 'copy', label: 'Копіювати посилання', icon: linkIcon, danger: false, onClick: handleCopyLink },
      { key: 'report', label: 'Поскаржитись', icon: reportIcon, danger: false, onClick: openReport },
    ];
    if (isAuthor) {
      arr.push({
        key: 'delete',
        label: 'Видалити',
        icon: deleteIcon,
        danger: true,
        onClick: () => {
          closeMenu();
          onDelete?.();
        },
      });
    }
    return arr;
  }, [isAuthor, handleCopyLink, openReport, onDelete, closeMenu]);

  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [sentToast, setSentToast] = useState(false);

  const sendReply = useCallback(async () => {
  const text = replyText.trim();
  if (!text || sending) return;

  if (!userId) {
    openAuthAlert();
    return;
  }

  setSending(true);
  try {
    const toUserId = itemAuthorId || authorId;

    const payload = {
      headlineId: String(id || headline?.id || ''),
      toUserId: String(toUserId || ''),
      fromUserId: String(userId || ''),
      text,
      createdAt: Date.now(),
    };

    const token = await getAccessToken().catch(() => null);

if (!token) {
  openAuthAlert();
  return;
}

await axios.post(
  `${API_BASE}/api/headlines/respond`,
  payload,
  {
    timeout: 12000,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }
);

    setReplyText('');
    setSentToast(true);
    setTimeout(() => setSentToast(false), 1400);

    setReplyMode(false);
  } catch {
  } finally {
    setSending(false);
  }
}, [replyText, sending, id, headline?.id, itemAuthorId, authorId, userId, openAuthAlert]);

    const onPointerDown = useCallback((e) => {
    if (swipeRef.current.active) return;
    if (isInteractiveTarget(e.target)) return;

    e.preventDefault?.();
    setPressPaused(true);
  }, []);

  const onPointerUp = useCallback(() => {
    setPressPaused(false);
  }, []);

  const onPointerCancel = useCallback(() => {
    setPressPaused(false);
  }, []);

  const onPointerLeave = useCallback(() => {
    setPressPaused(false);
  }, []);

  const progressCur = useMemo(() => {
    const p = Math.min(1, Math.max(0, elapsedMs / STORY_DURATION_MS));
    return p;
  }, [elapsedMs]);

  const effectiveAvatar = useMemo(() => {
    const a = String(authorAvatar || '').trim();
    return a || 'https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png';
  }, [authorAvatar]);

  const design = useMemo(() => {
    const d = cur?.design;
    return d && typeof d === 'object' ? d : null;
  }, [cur]);

  const photoTransformStyle = useMemo(() => {
    const p = design?.photo;
    if (!p) return null;

    const xPct = clampNum(p.xPct, 0, 100, 50);
    const yPct = clampNum(p.yPct, 0, 100, 50);
    const scale = clampNum(p.scale, 0.3, 4, 1);
    const rotate = clampNum(p.rotate, -180, 180, 0);

    return {
      transform: `translate(-50%, -50%) translate(${xPct - 50}%, ${yPct - 50}%) scale(${scale}) rotate(${rotate}deg)`,
    };
  }, [design]);

  const textTransformStyle = useMemo(() => {
    const t = design?.text;
    if (!t) return null;

    const xPct = clampNum(t.xPct, 0, 100, 50);
    const yPct = clampNum(t.yPct, 0, 100, 40);
    const scale = clampNum(t.scale, 0.3, 4, 1);
    const rotate = clampNum(t.rotate, -180, 180, 0);
    const color = String(t.color || '#FFFFFF');

    const boxW = clampNum(t.boxW, 40, 1000, 320);
    const boxH = clampNum(t.boxH, 30, 800, 140);

    return {
      left: `${xPct}%`,
      top: `${yPct}%`,
      width: `${boxW}px`,
      minHeight: `${boxH}px`,
      color,
      transform: `translate(-50%, -50%) scale(${scale}) rotate(${rotate}deg)`,
    };
  }, [design]);

  const useDesignedText = !!textTransformStyle;

  const onStoryPointerDown = useCallback((e) => {
  if (!isCoarsePointer()) return;

  if (isInteractiveTarget(e.target)) return;

  if (shareOpen || menuOpen || showReportUniversal || upgradeOpen) return;

  const el = storyRef.current;
  if (!el) return;

  const rect = el.getBoundingClientRect();
  swipeRef.current.height = rect.height || 720;

  swipeRef.current.active = true;
  swipeRef.current.pointerId = e.pointerId;
  swipeRef.current.startX = e.clientX;
  swipeRef.current.startY = e.clientY;
  swipeRef.current.lastX = e.clientX;
  swipeRef.current.lastY = e.clientY;
  swipeRef.current.startedAt = Date.now();
  swipeRef.current.directionLocked = false;
  swipeRef.current.isVertical = false;
  swipeRef.current.moved = false;
  swipeRef.current.shouldIgnore = false;

  try { el.setPointerCapture?.(e.pointerId); } catch {}

  setPressPaused(true);
}, [shareOpen, menuOpen, showReportUniversal, upgradeOpen]);

  const onStoryPointerMove = useCallback((e) => {
    if (!swipeRef.current.active) return;
    if (swipeRef.current.pointerId !== e.pointerId) return;

    const dx = e.clientX - swipeRef.current.startX;
    const dy = e.clientY - swipeRef.current.startY;

    swipeRef.current.lastX = e.clientX;
    swipeRef.current.lastY = e.clientY;

    const absX = Math.abs(dx);
    const absY = Math.abs(dy);

    if (!swipeRef.current.directionLocked) {
      if (absX + absY < 10) return;

      swipeRef.current.directionLocked = true;
      swipeRef.current.isVertical = absY > absX * 1.15;

      if (!swipeRef.current.isVertical) {
        swipeRef.current.shouldIgnore = true;
        return;
      }
    }

    if (swipeRef.current.shouldIgnore) return;

    if (dy <= 0) {
      setSwipeY(0);
      setSwipeDim(0);
      return;
    }

    swipeRef.current.moved = true;
    setSwipeClosing(true);

    const damp = dy > 180 ? 180 + (dy - 180) * 0.35 : dy;

    setSwipeY(damp);

    const h = swipeRef.current.height || 720;
    const k = Math.min(1, damp / (h * 0.55));
    setSwipeDim(k);

    e.preventDefault?.();
  }, []);

  const endSwipe = useCallback(() => {
    swipeRef.current.active = false;
    swipeRef.current.pointerId = null;
    swipeRef.current.directionLocked = false;
    swipeRef.current.isVertical = false;
    swipeRef.current.shouldIgnore = false;
    swipeRef.current.moved = false;
  }, []);

  const onStoryPointerUp = useCallback((e) => {
    if (!swipeRef.current.active) return;
    if (swipeRef.current.pointerId !== e.pointerId) return;

    const el = storyRef.current;
    try { el?.releasePointerCapture?.(e.pointerId); } catch {}

    const h = swipeRef.current.height || 720;
    const threshold = Math.max(120, h * 0.18);

    const shouldClose = swipeY >= threshold;

    endSwipe();

    if (shouldClose) {
      setSwipeClosing(true);
      setSwipeDim(1);
      setSwipeY(Math.max(swipeY, threshold + 80));

      window.setTimeout(() => {
        handleClose();
      }, 140);
    } else {
      resetSwipeVisual(true);
    }
  }, [swipeY, handleClose, endSwipe]);

  const onStoryPointerCancel = useCallback((e) => {
    if (!swipeRef.current.active) return;
    if (swipeRef.current.pointerId !== e.pointerId) return;
    const el = storyRef.current;
    try { el?.releasePointerCapture?.(e.pointerId); } catch {}
    endSwipe();
    resetSwipeVisual(true);
  }, [endSwipe]);

  const storyTransform = useMemo(() => {
    if (!swipeClosing || swipeY <= 0) return undefined;
    const y = Math.max(0, swipeY);
    const scale = Math.max(0.92, 1 - Math.min(0.08, y / 1800));
    return { transform: `translate3d(0, ${y}px, 0) scale(${scale})` };
  }, [swipeClosing, swipeY]);

  const backdropStyle = useMemo(() => {
    if (!swipeClosing || swipeY <= 0) return undefined;
    const base = 0.66;
    const alpha = Math.max(0.12, base * (1 - Math.min(1, swipeDim)));
    return { background: `rgba(0,0,0,${alpha})` };
  }, [swipeClosing, swipeY, swipeDim]);

  return (
    <div
      ref={backdropRef}
      className="hlm__backdrop is-story"
      role="dialog"
      aria-modal="true"
      onClick={onBackdropClick}
      style={backdropStyle}
    >
      <div
        ref={storyRef}
        className={`hlm__storyCard ${isPaused ? 'is-paused' : ''} ${replyMode ? 'is-replying' : ''} ${swipeClosing ? 'is-swipe' : ''}`}
        style={{ backgroundImage: gradient, ...(storyTransform || {}) }}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => { onStoryPointerDown(e); onPointerDown(e); }}
        onPointerMove={onStoryPointerMove}
        onPointerUp={(e) => { onStoryPointerUp(e); onPointerUp(e); }}
        onPointerCancel={(e) => { onStoryPointerCancel(e); onPointerCancel(e); }}
        onPointerLeave={onPointerLeave}
      >
        <div className="hlm__storyBg">
          {photoUrl ? (
            <>
              <div className="hlm__storyImgWrap">
                <img
                  className="hlm__storyImg"
                  src={photoUrl}
                  alt=""
                  style={photoTransformStyle || undefined}
                />
              </div>
              <div className="hlm__storyShade" />
            </>
          ) : null}

          <div className="hlm__storyFadeTop" />
          <div className="hlm__storyFadeBottom" />
        </div>

        <div className="hlm__progress">
          {Array.from({ length: Math.max(1, itemsLen) }).map((_, i) => {
            const done = i < idx;
            const active = i === idx;
            const fill = done ? 1 : active ? progressCur : 0;
            return (
              <div key={i} className="hlm__seg">
                <div className="hlm__segFill" style={{ transform: `scaleX(${fill})` }} />
              </div>
            );
          })}
        </div>

        <div className="hlm__topRow">
          <button
            className="hlm__avaBtn"
            type="button"
            onClick={() => onGoProfile?.(itemAuthorId)}
            aria-label={`Перейти в профіль ${authorName || 'Користувач'}`}
          >
            <span className="hlm__avaRing" />
            <img className="hlm__avaImg" src={effectiveAvatar} alt={authorName || 'Автор'} loading="lazy" />
          </button>

          <div className="hlm__topMeta">
            <div className="hlm__nick" title={authorName || 'Користувач'}>
              {authorName || 'Користувач'}
            </div>

            <div className="hlm__line1">
              <time className="hlm__ago">{timeAgoSmart(createdAt, Date.now())}</time>

              {isAuthor ? (
                <span className="hlm__viewsInline" title="Перегляди">
                  <img src={viewIcon} alt="" />
                  <span>{Number(displayedViews || 0)}</span>
                </span>
              ) : null}
            </div>

            <div className="hlm__line2">
              <span className="hlm__type">{accountType || 'Користувач'}</span>
              {cityText ? (
                <>
                  <span className="hlm__dot">•</span>
                  <span className="hlm__city">{cityText}</span>
                </>
              ) : null}
            </div>
          </div>

          <div className="hlm__topActions">
            <button
              className="hlm__topBtn hlm__topBtn--plus"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleNewOrUpgrade();
              }}
              title="Новий Headline"
              aria-label="Новий Headline"
              type="button"
            >
              <span className="hlm__topBtnPlus" aria-hidden="true">+</span>
            </button>
            <button
              className="hlm__topBtn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShareOpen(true);
              }}
              title="Поділитись"
              aria-label="Поділитись"
              type="button"
            >
              <img src={shareIcon} alt="" />
            </button>

            <div className="hlm__moreWrap">
              <button
                ref={moreBtnRef}
                className="hlm__topBtn"
                aria-haspopup="menu"
                aria-expanded={menuOpen ? 'true' : 'false'}
                aria-label="Більше дій"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleMenu();
                }}
                title={copyState.ok ? 'Скопійовано!' : copyState.error ? copyState.error : 'Більше дій'}
                type="button"
              >
                <img src={moreIcon} alt="" />
              </button>

              {menuOpen && (
                <div
                  ref={menuRef}
                  className="hlm__menu"
                  role="menu"
                  aria-label="Меню дій"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {menuItems.map((mi) => (
                    <button
                      key={mi.key}
                      className={`hlm__menuItem ${mi.danger ? 'is-danger' : ''}`}
                      role="menuitem"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        mi.onClick?.();
                        closeMenu();
                      }}
                      type="button"
                    >
                      <img
                        src={mi.icon}
                        alt=""
                        className={`hlm__menuIco ${mi.key === 'copy' ? 'hlm__menuIco--invert' : ''}`}
                      />
                      <span>{mi.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              className="hlm__closeBtn"
              type="button"
              aria-label="Закрити"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClose();
              }}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
        </div>

                <div
          className="hlm__canvas"
          onMouseDown={(e) => e.preventDefault()}
          onTouchStart={(e) => e.preventDefault()}
        >
          {useDesignedText ? (
            <div
              className="hlm__overlayText"
              style={textTransformStyle || undefined}
              title={displayTitle}
              aria-hidden="true"
            >
              {displayTitle}
            </div>
          ) : (
            <div className="hlm__center hlm__center--passive" aria-hidden="true">
              <div className="hlm__titleWrap">
                <div className="hlm__title" title={displayTitle}>
                  {displayTitle}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="hlm__dimLayer" />

        {!isAuthor && (
          <div
            className="hlm__replyBar"
            onClick={(e) => {
              e.stopPropagation();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div className="hlm__replyGlass">
              <input
                className="hlm__replyInput"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Відгукнутись…"
                onFocus={() => {
                  setReplyMode(true);
                }}
                onBlur={() => {
                  setTimeout(() => setReplyMode(false), 0);
                }}
              />

              <button
                className="hlm__sendBtn"
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={sendReply}
                aria-label="Надіслати"
                disabled={!replyText.trim() || sending}
              >
                <img src={sendIcon} alt="" />
              </button>
            </div>

            {sentToast ? <div className="hlm__toast">Повідомлення відправлено</div> : null}
          </div>
        )}

        {isMulti && (
          <>
            <button
              className="hlm__tap hlm__tap--left"
              aria-label="Попередній"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                goPrev();
              }}
              type="button"
            />
            <button
              className="hlm__tap hlm__tap--right"
              aria-label="Наступний"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                goNext();
              }}
              type="button"
            />
          </>
        )}
      </div>

      {shareOpen && (
        <div
          className="hlm__portal-stop"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} shareUrl={shareUrl} shareText={shareText} />
        </div>
      )}

      {showReportUniversal && (
        <div
          className="hlm__portal-stop"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <ReportsModal
            open={showReportUniversal}
            onClose={() => setShowReportUniversal(false)}
            onSubmitted={() => setShowReportUniversal(false)}
            userId={userId}
            targetType="headline"
            targetId={id || headline?.id}
            title="Поскаржитись на Headline"
          />
        </div>
      )}

            {upgradeOpen && (
        <div
          className="hlm__portal-stop"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Ліміт Headline"
        >
          <div className="hlm__upgrade-backdrop" onClick={() => setUpgradeOpen(false)}>
            <div className="hlm__upgrade-card" onClick={(e) => e.stopPropagation()}>
              <div className="hlm__upgrade-title">Наразі</div>
              <div className="hlm__upgrade-text">
                У вас може бути не більше одного активного Headline.
              </div>
              <div className="hlm__upgrade-actions">
                <button
                  type="button"
                  className="hlm__btn primary"
                  onClick={() => setUpgradeOpen(false)}
                >
                  Зрозуміло
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Alerts
    open={authAlertOpen}
    variant="headlineLoginRequired"
    onClose={() => setAuthAlertOpen(false)}
    onCancel={() => setAuthAlertOpen(false)}
    onPrimary={() => {
    setAuthAlertOpen(false);

    const next =
      typeof window !== 'undefined'
        ? encodeURIComponent(window.location.pathname + window.location.search)
        : '';

    if (typeof window !== 'undefined') {
      window.location.href = `/auth?next=${next}`;
    }
  }}
/>

    </div>
  );
}
